import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  MoreVert,
  Reply,
  Delete,
  AccessTime,
} from '@mui/icons-material';
import { Comment } from '@/constants/types';
import { useAppDispatch, useAppSelector, deleteComment } from '@/store';
import { CommentForm } from './CommentForm';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

interface CommentItemProps {
  comment: Comment;
  feedId: string;
  level?: number;
  maxLevel?: number;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  feedId,
  level = 0,
  maxLevel = 3,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { isDeleting } = useAppSelector((state) => state.comments);
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  // Check ownership - user.id should match comment.userId._id
  const isOwner = user?.id === comment.userId._id;
  
  console.log('Ownership check:', {
    userId: user?.id,
    commentUserId: comment.userId._id,
    isOwner
  });
  const hasReplies = comment.replies && comment.replies.length > 0;
  const canNestDeeper = level < maxLevel;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      await dispatch(deleteComment(comment._id));
    }
    handleMenuClose();
  };

  const handleReply = () => {
    setShowReplyForm(true);
  };

  const formatTime = (dateString: string) => {
    return moment(dateString).fromNow();
  };

  const getProfilePictureUrl = () => {
    return comment.userId.profilePictureId?.url;
  };

  return (
    <Box 
      sx={{ mb: level === 0 ? 1 : 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack direction="row" spacing={1.5}>
        <Box sx={{ position: 'relative', p:1 }}>
          {comment.userId.isLookingForJob ? (
            <Box
              sx={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
              onClick={() => navigate('/profile/' + comment.userId._id)}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: level > 0 ? -3 : -3,
                  left: level > 0 ? -3 : -3,
                  right: level > 0 ? -3 : -3,
                  bottom: level > 0 ? -3 : -3,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  padding: '3px',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: theme.palette.background.paper,
                  }}
                />
              </Box>
              <Avatar
                src={getProfilePictureUrl()}
                sx={{
                  width: level > 0 ? 36 : 44,
                  height: level > 0 ? 36 : 44,
                  fontSize: level > 0 ? '0.875rem' : '1.125rem',
                  fontWeight: 700,
                  bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: '3px solid',
                  borderColor: 'background.paper',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  zIndex: 1,
                  ...(isHovered && {
                    transform: 'scale(1.05)',
                  }),
                }}
              >
                {comment.userId.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: level > 0 ? -6 : -7,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  color: 'white',
                  fontSize: level > 0 ? '5px' : '6px',
                  fontWeight: 700,
                  padding: level > 0 ? '1px 4px' : '1px 5px',
                  borderRadius: level > 0 ? '6px' : '7px',
                  whiteSpace: 'nowrap',
                  zIndex: 2,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                OPEN TO WORK
              </Box>
            </Box>
          ) : (
            <Avatar
              src={getProfilePictureUrl()}
              onClick={() => navigate('/profile/' + comment.userId._id)}
              sx={{
                width: level > 0 ? 36 : 44,
                height: level > 0 ? 36 : 44,
                fontSize: level > 0 ? '0.875rem' : '1.125rem',
                fontWeight: 700,
                bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: '3px solid',
                borderColor: 'background.paper',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                ...(isHovered && {
                  transform: 'scale(1.05)',
                }),
              }}
            >
              {comment.userId.name.charAt(0).toUpperCase()}
            </Avatar>
          )}
        </Box>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Comment Bubble */}
          <Box 
            sx={{ 
              position: 'relative',
              bgcolor: level === 0 
                ? (theme) => alpha(theme.palette.primary.main, 0.02)
                : (theme) => alpha(theme.palette.grey[100], 0.8),
              borderRadius: 3,
              px: 2.5,
              py: 2,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: level === 0 
                  ? (theme) => alpha(theme.palette.primary.main, 0.04)
                  : (theme) => alpha(theme.palette.grey[50], 1),
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: -8,
                top: 16,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '8px 8px 8px 0',
                borderColor: `transparent ${level === 0 
                  ? alpha('#1976d2', 0.02)
                  : alpha('#f5f5f5', 0.8)} transparent transparent`,
              },
            }}
          >
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="flex-start"
              sx={{ mb: 0.5 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 700,
                      color: 'text.primary',
                      lineHeight: 1.2,
                      background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {comment.userId.name}
                  </Typography>
                  
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <AccessTime 
                      sx={{ 
                        fontSize: '0.75rem', 
                        color: 'text.secondary',
                        opacity: 0.7 
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        opacity: 0.8,
                      }}
                    >
                      {formatTime(comment.createdAt)}
                    </Typography>
                  </Stack>
                </Stack>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.primary',
                    lineHeight: 1.6,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem',
                    fontWeight: 400,
                  }}
                >
                  {comment.content}
                  {comment.isEdited && (
                    <Typography 
                      component="span" 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        ml: 1,
                        fontStyle: 'italic',
                        fontSize: '0.75rem',
                        opacity: 0.7,
                      }}
                    >
                      (edited)
                    </Typography>
                  )}
                </Typography>
              </Box>
              
              {isOwner && (
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{ 
                    ml: 1,
                    color: 'text.secondary',
                    opacity: isHovered ? 1 : 0.5,
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                      color: 'error.main',
                    },
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Box>
          
          {/* Comment Actions */}
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ mt: 1.5, px: 1, opacity: isHovered ? 1 : 0.7, transition: 'opacity 0.2s ease' }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {hasReplies && comment.stats?.replies && (
                <Button
                  size="small"
                  onClick={() => setShowReplies(!showReplies)}
                  sx={{
                    textTransform: 'none',
                    color: 'primary.main',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {showReplies ? '👁️ Hide' : '👀 Show'} {comment.stats.replies} {comment.stats.replies === 1 ? 'reply' : 'replies'}
                </Button>
              )}
            </Stack>
            
            <Button
              size="small"
              startIcon={<Reply sx={{ fontSize: '0.75rem !important' }} />}
              onClick={handleReply}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 'auto',
                px: 2,
                py: 0.75,
                borderRadius: 2,
                border: '1px solid transparent',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              💬 Reply
            </Button>
          </Stack>
          
          {/* Reply Form */}
          <Collapse in={showReplyForm}>
            <Box sx={{ mt: 2.5, ml: 1 }}>
              <CommentForm
                feedId={feedId}
                parentCommentId={comment._id}
                placeholder={`💭 Reply to ${comment.userId.name}...`}
                onCancel={() => setShowReplyForm(false)}
                autoFocus
              />
            </Box>
          </Collapse>
          
          {/* Replies */}
          {hasReplies && canNestDeeper && (
            <Collapse in={showReplies}>
              <Box sx={{ 
                mt: 3, 
                ml: 2, 
                borderLeft: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                pl: 2.5,
                position: 'relative',
                // '&::before': {
                //   content: '""',
                //   position: 'absolute',
                //   left: -3,
                //   top: 0,
                //   bottom: 0,
                //   width: 3,
                //   background: 'linear-gradient(to bottom, #667eea, #764ba2)',
                //   borderRadius: '0 2px 2px 0',
                // },
              }}>
                {comment.replies?.map((reply, index) => (
                  <Box key={reply._id} sx={{ 
                    ...(index > 0 && { 
                      borderTop: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                      pt: 2,
                      mt: 2,
                    })
                  }}>
                    <CommentItem
                      comment={reply}
                      feedId={feedId}
                      level={level + 1}
                      maxLevel={maxLevel}
                    />
                  </Box>
                ))}
              </Box>
            </Collapse>
          )}
        </Box>
      </Stack>
      
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              // minWidth: 140,
              // border: '1px solid',
              // borderColor: 'divider',
            }
          }
        }}
      >
        <MenuItem 
          onClick={handleDelete}
          disabled={isDeleting}
          sx={{ 
            color: 'error.main',
            // fontSize: '0.875rem',
            // fontWeight: 600,
            // borderRadius: 2,
            // mx: 1,
            // my: 0.5,
            // '&:hover': {
            //   bgcolor: 'error.light',
            //   color: 'error.contrastText',
            // },
          }}
        >
          <Delete fontSize="small" sx={{ mr: 1.5 }} />
           Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};